import classNames from "classnames";
import React, {
  ChangeEvent,
  FC,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "react-query";
import axios from "axios";
import classes from "./ModalEmployees.module.scss";
import modalBase from "../../../../ModalBase/ModalBase.module.scss";
import CustomRadio from "../../../CustomRadio/CustomRadio";
import { CustomTextField } from "../../../CustomTextField/CustomTextField";
import { Button } from "../../../Button/Button";
import * as yup from "yup";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ModalEmployeeSchema } from "../../../../../schemas/ModalEmployeeSchema";
import { EmployerType } from "../../../../../pagesAdmin/EmployersPage/EmployersPage";
import { CreateNewUserType } from "../../../../../hooks/Admin/EmployeesTable/usePostNewEmployer";
import { UpdateUserDataType } from "../../../../../hooks/Admin/EmployeesTable/usePutUserData";
import {
  PermissionMatrix,
  PermissionRow,
} from "../../../Permissions/PermissionMatrix/PermissionMatrix";
import { AdminAPI, UserPermissionsAPI } from "../../../../../constants/API";
import { commonHeaders } from "../../../../../constants/HeadersAPI";
import { getUserData } from "../../../../../utils/Authorization/authorization";
import BranchQueueTreeSelect from "../../../BranchQueueTreeSelect/BranchQueueTreeSelect";
import { useAdminQueueIdsFilter } from "../../../../../hooks/Admin/useAdminQueueIdsFilter";
import { EmployeePhotoEditor } from "../../../EmployeePhotoEditor/EmployeePhotoEditor";
import { resolveMediaUrl } from "../../../../../utils/mediaUrl";

type ModalEmployeesProps = {
  isActive: boolean;
  setActive: (value: boolean) => void;
  activeRowDataEmployer?: EmployerType;
  createNewEmployer?: (userData: CreateNewUserType) => void;
  updateUserData?: (userData: UpdateUserDataType) => void;
  modalMode: string;
  isLoadingCreate: boolean;
  isLoadingUpdate: boolean;
};

const RadioButtons = [
  {
    type: "operator",
    label: "Оператор",
  },
  {
    type: "adminBranch",
    label: "Администратор ",
  },
];

const normalizeRoleLabel = (roleDescription?: string): string => {
  const value = (roleDescription ?? "").trim();
  if (value.toLowerCase().startsWith("администратор")) return "Администратор ";
  return "Оператор";
};
export const ModalEmployees: FC<ModalEmployeesProps> = ({
  isActive,
  setActive,
  activeRowDataEmployer,
  modalMode,
  createNewEmployer,
  updateUserData,
  isLoadingCreate,
  isLoadingUpdate,
}) => {
  const [selectedRadio, setSelectedRadio] = useState("Оператор");
  const [selectedBranchIds, setSelectedBranchIds] = useState<number[]>([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState<number[]>([]);
  const [selectedBranchesValidationError, setSelectedBranchesValidationError] =
    useState<string | null>(null);
  const [permissionRows, setPermissionRows] = useState<PermissionRow[]>([]);
  const [employeePhoto, setEmployeePhoto] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const initialBranchIdsSetForCreate = useRef(false);

  const userData = getUserData();
  const currentUserId =
    typeof userData !== "string" && typeof userData?.id === "number"
      ? (userData.id as number)
      : null;
  const currentUserRole =
    typeof userData !== "string" && typeof userData?.role === "string"
      ? (userData.role as string)
      : "";
  const isBranchAdmin = currentUserRole.trim().startsWith("Администратор");
  const { queueIdsFilter } = useAdminQueueIdsFilter();
  const userBranchIds =
    typeof userData !== "string" ? (userData?.branchIds ?? []) : [];
  const branchIdsFilter =
    isBranchAdmin && userBranchIds.length > 0 ? userBranchIds : undefined;

  const userId = activeRowDataEmployer?.id;
  useQuery<number[]>(
    ["userBranches", userId],
    () =>
      axios
        .get(AdminAPI.getUserBranches(userId!), commonHeaders())
        .then((r) => r.data),
    {
      enabled: isActive && modalMode === "edit" && !!userId,
      onSuccess: (data) => setSelectedBranchIds(data || []),
    },
  );
  useQuery<{ permissionKey: string; canView: boolean; canEdit: boolean }[]>(
    ["userPermissions", userId],
    () =>
      axios
        .get(UserPermissionsAPI.getPermissionsForUser(userId!), commonHeaders())
        .then((r) => r.data),
    {
      enabled: isActive && modalMode === "edit" && !!userId,
      onSuccess: (data) => {
        setPermissionRows(data || []);
      },
    },
  );
  useQuery<number[]>(
    ["userQueues", userId],
    () =>
      axios
        .get(AdminAPI.getUserQueues(userId!), commonHeaders())
        .then((r) => r.data),
    {
      enabled: isActive && modalMode === "edit" && !!userId,
      onSuccess: (data) => setSelectedQueueIds(data ?? []),
    },
  );

  type FormData = yup.InferType<typeof ModalEmployeeSchema>;
  const {
    register,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(ModalEmployeeSchema),
    mode: "onChange",
    defaultValues: {
      employeeName: "",
      changePassword: true,
      password: "",
    },
  });
  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSelectedRadio(e.target.value);
    setSelectedBranchesValidationError(null);
  };

  const permissionsPayload = useMemo(
    () =>
      permissionRows.map((r) => ({
        permissionKey: r.permissionKey,
        canView: r.canView,
        canEdit: r.canEdit,
      })),
    [permissionRows],
  );

  const isSelfEdit =
    modalMode === "edit" &&
    !!activeRowDataEmployer?.id &&
    currentUserId !== null &&
    currentUserId === activeRowDataEmployer.id;

  const isAdminRole = currentUserRole.startsWith("Администратор");

  const isEditingSelfAdmin = isSelfEdit && isAdminRole;

  const onSubmit: SubmitHandler<FormData> = async (data: FormData) => {
    if (!isEditingSelfAdmin && selectedBranchIds.length === 0) {
      setSelectedBranchesValidationError("Поле обязательно для заполнения");
      return;
    }

    const { employeeName, password, changePassword } = data as FormData & {
      changePassword?: boolean;
    };
    const isChangePasswordEnabled =
      modalMode === "create" || changePassword === true;
    const passwordForRequest = isChangePasswordEnabled ? password : null;
    const baseBody = {
      password: passwordForRequest,
      fullName: employeeName,
      role: selectedRadio,
    };
    const body = isEditingSelfAdmin
      ? baseBody
      : {
          ...baseBody,
          branchIds: selectedBranchIds,
          permissions:
            selectedRadio === "Администратор " ? permissionsPayload : undefined,
        };
    const initialPhotoUrl = resolveMediaUrl(activeRowDataEmployer?.photoUrl ?? null);

    if (modalMode === "edit" && activeRowDataEmployer?.id) {
      updateUserData({
        userId: activeRowDataEmployer.id,
        body,
        queueIds: selectedQueueIds,
        photo: employeePhoto,
        initialPhotoUrl,
      });
    }
    if (modalMode === "create") {
      createNewEmployer({
        password,
        fullName: employeeName,
        role: selectedRadio,
        branchIds: selectedBranchIds,
        queueIds: selectedQueueIds.length > 0 ? selectedQueueIds : undefined,
        permissions:
          selectedRadio === "Администратор " ? permissionsPayload : undefined,
        employeePhoto,
      });
    }
  };
  const handleClickOutside = (event: MouseEvent): void => {
    if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
      setActive(false);
    }
  };

  const handleEmployeeNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue("employeeName", e.target.value);
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue("password", e.target.value);
  };

  const handleChangePasswordToggle = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setValue("changePassword", checked);
    if (!checked) {
      setValue("password", "");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (modalMode === "edit" && activeRowDataEmployer) {
      setValue("employeeName", activeRowDataEmployer.employee || "");
      setSelectedRadio(normalizeRoleLabel(activeRowDataEmployer?.accessLevel));
      setValue("changePassword", false);
      setValue("password", "");
      setSelectedBranchesValidationError(null);
      setEmployeePhoto(resolveMediaUrl(activeRowDataEmployer.photoUrl ?? null));
      return;
    }
    if (modalMode === "create") {
      setValue("employeeName", "");
      setValue("changePassword", true);
      setValue("password", "");
      setEmployeePhoto(null);
    }
  }, [activeRowDataEmployer, modalMode, setValue]);

  useEffect(() => {
    if (!isActive) {
      setValue("employeeName", "");
      setValue("password", "");
      setValue("changePassword", true);
      setSelectedRadio("Оператор");
      setSelectedBranchIds([]);
      setSelectedQueueIds([]);
      setSelectedBranchesValidationError(null);
      setPermissionRows([]);
      setEmployeePhoto(null);
      initialBranchIdsSetForCreate.current = false;
    }
  }, [isActive]);

  useEffect(() => {
    if (!isActive || modalMode !== "create") return;
    setValue("employeeName", "");
    setValue("password", "");
    setValue("changePassword", true);
    if (initialBranchIdsSetForCreate.current) return;
    setSelectedBranchIds([]);
    setSelectedQueueIds([]);
    setSelectedBranchesValidationError(null);
    setPermissionRows([]);
    initialBranchIdsSetForCreate.current = true;
  }, [isActive, modalMode]);

  return (
    <div
      className={classNames(modalBase.modal, { [modalBase.active]: isActive })}
    >
      <div
        className={classNames(modalBase.container, classes.modalContainer)}
        ref={modalRef}
      >
        <form className={classes.employeesForm}>
          {(isLoadingCreate || isLoadingUpdate) && (
            <div className={classes.load}>
              <div className={classes.loadContent}>
                <p>Cохранение...</p>
              </div>
            </div>
          )}
          <h2 className={modalBase.title}>Сотрудник</h2>
          <div className={modalBase.scrollArea}>
            {!isEditingSelfAdmin && (
              <div className={classes.radioContainer}>
                {RadioButtons.map((item, key) => (
                  <div key={key} className={classes.radio}>
                    <CustomRadio
                      id={item.type}
                      value={item.label}
                      checked={selectedRadio === item.label}
                      onChange={handleRadioChange}
                      label={item.label}
                    />
                  </div>
                ))}
              </div>
            )}

            <div className={classes.workpaceName}>
              <CustomTextField
                {...register("employeeName")}
                name="employeeName"
                value={watch("employeeName")}
                onChange={handleEmployeeNameChange}
                labelName={"Фамилия, инициалы сотрудника (логин)"}
                errorMessage={errors.employeeName?.message}
              />
            </div>

            <EmployeePhotoEditor
              key={
                modalMode === "create"
                  ? "create"
                  : `edit-${activeRowDataEmployer?.id ?? "unknown"}`
              }
              idPrefix="modal-employee"
              value={employeePhoto}
              onChange={setEmployeePhoto}
            />

            <div className={classes.passwordContainer}>
              {modalMode === "edit" && (
                <label className={classes.changePasswordToggle}>
                  <input
                    type="checkbox"
                    checked={watch("changePassword")}
                    onChange={handleChangePasswordToggle}
                  />
                  <span>Изменить пароль</span>
                </label>
              )}
              {(modalMode === "create" || watch("changePassword")) && (
                <div className={classes.windowNum}>
                  <CustomTextField
                    {...register("password")}
                    name="password"
                    value={watch("password")}
                    onChange={handlePasswordChange}
                    labelName={"Пароль"}
                    errorMessage={errors.password?.message}
                  />
                </div>
              )}
            </div>

            {!isEditingSelfAdmin && (
              <BranchQueueTreeSelect
                selectedBranchIds={selectedBranchIds}
                selectedQueueIds={selectedQueueIds}
                onBranchChange={(ids) => {
                  setSelectedBranchIds(ids);
                  if (ids.length > 0) setSelectedBranchesValidationError(null);
                }}
                onQueueChange={setSelectedQueueIds}
                branchIdsFilter={branchIdsFilter}
                queueIdsFilter={queueIdsFilter}
                className={classes.branchQueueBlock}
                errorMessage={selectedBranchesValidationError}
              />
            )}

            {!isEditingSelfAdmin && selectedRadio === "Администратор " && (
              <PermissionMatrix
                value={permissionRows}
                onChange={setPermissionRows}
              />
            )}
          </div>

          <div
            className={classNames(modalBase.footer, classes.buttonsContainer)}
          >
            <Button
              content={"отмена"}
              btnStyle={"white"}
              onClick={() => setActive(false)}
            />
            <Button
              content={"сохранить"}
              btnStyle={"blue"}
              onClick={handleSubmit(onSubmit)}
              isLoading={isLoadingCreate || isLoadingUpdate}
            />
          </div>
        </form>
      </div>
    </div>
  );
};
